(() => {
  customElements.whenDefined('basic-modal').then(() => {
    if (customElements.get('ss-look-related-modal')) return;

    class LookRelatedModal extends customElements.get('basic-modal') {
      connectedCallback() {
        super.connectedCallback();
        this.cache ||= new Map();
        this.grid = this.querySelector('[data-look-related-grid]');
        this.body = this.querySelector('[data-look-related-body]');
        this.loading = this.querySelector('[data-look-related-loading]');
        this.empty = this.querySelector('[data-look-related-empty]');
        this.error = this.querySelector('[data-look-related-error]');
        this.querySelector('[data-look-related-retry]').addEventListener('click', () => this.load(), { signal: this.abortController.signal });
        this.addEventListener('keydown', event => this.keepFocus(event), { signal: this.abortController.signal });
      }

      keepFocus(event) {
        if (!this.open || event.key !== 'Tab') return;
        // Results arrive after opening, so calculate the visible controls afresh.
        event.stopPropagation();
        const controls = window.FoxTheme.a11y.getFocusableElements(this).filter(element =>
          element.tabIndex >= 0 && !element.closest('[hidden], [inert]') && element.getClientRects().length > 0
        );
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && event.target === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && event.target === last) {
          event.preventDefault();
          first?.focus();
        }
      }

      onButtonClick(event) {
        if (event.currentTarget.matches('a') && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
        super.onButtonClick(event);
      }

      show(trigger, animate = true) {
        if (!this.open && trigger?.dataset.lookRelatedUrl) {
          this.productURL = trigger.dataset.lookRelatedUrl;
          this.querySelector('[data-look-related-product]').textContent = trigger.dataset.lookRelatedTitle || '';
          this.load();
        }
        return super.show(trigger, animate);
      }

      prepareToHide() {
        super.prepareToHide();
        this.request?.abort();
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        // The theme moves open modals to <body>; only cancel on actual removal.
        queueMicrotask(() => { if (!this.isConnected) this.request?.abort(); });
      }

      async load() {
        this.request?.abort();
        const request = new AbortController();
        this.request = request;
        this.grid.replaceChildren();
        this.loading.hidden = false;
        this.empty.hidden = true;
        this.error.hidden = true;
        this.body.setAttribute('aria-busy', 'true');
        this.body.scrollTop = 0;
        if (this.open && this.error.contains(document.activeElement)) this.focusElement.focus();
        const timer = setTimeout(() => request.abort('timeout'), 15000);
        try {
          const url = new URL(this.productURL, window.location.origin);
          if (url.origin !== window.location.origin) throw new Error('Invalid product URL');
          url.hash = '';
          url.search = '';
          // Render the existing PDP section: its Search & Discovery order and
          // same-brand fallback remain the single source of truth.
          url.searchParams.set('section_id', 'related-products');
          const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
          let html = this.cache.get(url.href);
          if (html === undefined) {
            const response = await fetch(url.href, { credentials: 'same-origin', signal: request.signal });
            if (!response.ok) throw new Error('Related products unavailable');
            html = await response.text();
          }
          if (request.signal.aborted || this.request !== request || !this.isConnected) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = doc.getElementById('shopify-section-related-products');
          if (!section || section.textContent.includes('Liquid error')) throw new Error('Invalid related products section');
          const cards = [...section.querySelectorAll('.related-products .products-grid > .f-column > .product-card')];
          const content = document.createDocumentFragment();
          cards.forEach(card => content.append(document.importNode(card, true)));
          this.grid.replaceChildren(content);
          this.empty.hidden = cards.length > 0;
          this.cache.set(url.href, html);
          this.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
        } catch (error) {
          if (this.request === request && this.isConnected && (!request.signal.aborted || request.signal.reason === 'timeout')) {
            this.error.hidden = false;
          }
        } finally {
          clearTimeout(timer);
          if (this.request === request) {
            this.loading.hidden = true;
            this.body.removeAttribute('aria-busy');
          }
        }
      }
    }
    customElements.define('ss-look-related-modal', LookRelatedModal);
  });
})();
